// @file Composable for defining keyboard shortcuts and their effects.
import { isAppUsing } from '@@/bits/flip'
import type OzInput from '@@/library/v4/components/OzInput.vue'
import tinykeys from 'tinykeys'
import type { Ref } from 'vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'

interface Shortcut {
  key: string
  callback: (event: KeyboardEvent) => void
  triggerOnce?: boolean
  clearCache?: boolean
}

export const useKeyboardShortcuts = (shortcuts: Shortcut[]): void => {
  let removeKeyboardShortcuts: (() => void) | null = null
  const shortcutTriggerRecord = ref<Record<string, boolean>>({}) // tracking for shortcuts to be only triggered once

  onMounted(() => {
    const keyboardShortcuts = shortcuts.reduce<Record<string, (event: KeyboardEvent) => void>>(
      (acc, { key, callback, triggerOnce = false, clearCache = false }) => {
        acc[key] = (event: KeyboardEvent) => {
          // For shorcuts that should only trigger once, we don't want to trigger the callback if the same shortcut is pressed again.
          if (triggerOnce) {
            if (shortcutTriggerRecord.value[key]) {
              // The shortcut is being triggered for the second time. Reset the trigger record, skip the callback.
              shortcutTriggerRecord.value[key] = false
              return
            }
            shortcutTriggerRecord.value[key] = true
          }

          if (clearCache) {
            shortcutTriggerRecord.value = {}
          }

          callback(event)
        }
        return acc
      },
      {},
    )

    removeKeyboardShortcuts = tinykeys(document.documentElement, keyboardShortcuts)
  })

  onBeforeUnmount(() => {
    removeKeyboardShortcuts?.()
  })
}

export const useKeyboardShortcutsForSearch = (
  searchInput: Ref<InstanceType<typeof OzInput> | null | undefined>,
): void => {
  useKeyboardShortcuts([
    {
      key: '/',
      callback: (event: KeyboardEvent) => {
        const isSearchInputFocused = searchInput.value?.$el.contains(document.activeElement) ?? false
        if (!isSearchInputFocused) {
          event.preventDefault()
          searchInput.value?.focusInput()
        }
      },
      triggerOnce: true,
    },
    {
      key: '$mod+/',
      callback: (event: KeyboardEvent) => {
        event.preventDefault()
        searchInput.value?.focusInput()
      },
      triggerOnce: true,
    },
    {
      key: '$mod+KeyF',
      callback: (event: KeyboardEvent) => {
        event.preventDefault()
        searchInput.value?.focusInput()
      },
      triggerOnce: true,
    },
    {
      key: 'Escape',
      callback: () => {
        searchInput.value?.blurInput()
      },
      clearCache: true,
    },
  ])
}

export const useKeyboardShortcutsForUndoRedo = ({
  handleUndo,
}: {
  handleUndo: (event: KeyboardEvent) => void
}): void => {
  if (!isAppUsing('undoRedoDeleteActions')) return

  useKeyboardShortcuts([
    {
      key: '$mod+KeyZ',
      callback: handleUndo,
    },
  ])
}
